<template>
  <div class="border border-gray-100 rounded-xs p-4 shadow-xs">
    <div class="relative flex flex-col align-items-center justify-center bg-gray-50 text-gray-700 text-18 font-heavy tracking-tight rounded-t-sm p-sm">
      <!-- Special status bar with icon -->
      <div v-if="currentWrestlerEventBooking.status === 'completed'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-green-300 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0" />
        </svg>
        <div class="text-12 text-green-900 upper-label">Complete</div>
      </div>
      <div v-else-if="currentWrestlerEventBooking.status === 'confirmed'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-blue-300 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-blue-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <div class="text-12 text-blue-900 upper-label">Confirmed</div>
      </div>
      <div v-else-if="currentWrestlerEventBooking.status === 'invited'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-blue-200 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-blue-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16 12H8m8 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
        </svg>
        <div class="text-12 text-blue-900 upper-label">Invited</div>
      </div>
      <div v-else-if="currentWrestlerEventBooking.status === 'declined'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-orange-300 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-orange-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <div class="text-12 text-orange-900 upper-label">Declined</div>
      </div>
      <div v-else-if="currentWrestlerEventBooking.status === 'cancelled'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-red-300 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-red-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
        <div class="text-12 text-red-900 upper-label">Cancelled</div>
      </div>
      <div v-else-if="currentWrestlerEventBooking.status === 'pending'"
            class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-gray-400 rounded-t-sm">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-900">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" fill="none" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h6" />
        </svg>
        <div class="text-12 text-gray-900 upper-label">Pending</div>
      </div>
      <!-- Day of week always shown, with extra margin if status bar is present -->
      <div class="upper-label" :class="{ 'm-t-lg': showStatusBar }">{{ eventDayOfWeek }}</div>
      <div class="text-18">{{ eventMonthDay }}</div>
      <div class="text-14 m-t-xxs">{{ eventTime }}</div>
    </div>

    <div class="flex justify-space-between align-items-start p-x-sm p-y-xs">
      <div class="flex flex-col gap-y-xxs flex-1 min-w-0">
        <div class="text-14 text-gray-700 flex gap-x-sm align-items-center flex-wrap">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="flex-shrink-0 h-6 w-6 text-gray-300" fill="currentColor">
            <path d="M634 326.36l-150-120a16.16 16.16 0 0 0-18.2-1.2l-27.4 16.21a8 8 0 0 1-9.1-.6l-25.2-20.21a8 8 0 0 1-2.6-8.9l10.2-29.51a16 16 0 0 0-5.1-17.7L320 80.8c-62.6-40.1-141.7-42.9-207-9.3C100.9 56.9 83.2 48 64 48a64.06 64.06 0 0 0-64 64 63.27 63.27 0 0 0 23 49c-40.1 78.24-27.5 176.56 38 242 89.8 89.82 251.11 81.42 326.51-32l150.41 86a15.93 15.93 0 0 0 20.7-4.3l78.2-104.33a16 16 0 0 0-2.82-22.01zM40.4 133.22A31.86 31.86 0 0 1 85.5 88.3c-17.8 13.01-29.9 24.32-45.1 44.92zM208 352a96 96 0 1 1 96-96 96 96 0 0 1-96 96z" class="fa-secondary"/>
            <path d="M208 160a96 96 0 1 0 96 96 96 96 0 0 0-96-96zm0 144a48 48 0 1 1 48-48 48 48 0 0 1-48 48z" class="fa-primary"/>
          </svg>

          <p class="m-y-none">{{ coachNames }}</p>
        </div>
        <div class="text-14 text-gray-700 flex gap-x-sm align-items-center flex-wrap">
          <wrestler-icon stroke-color="text-gray-700"></wrestler-icon>
          <p class="m-y-none break-words flex-1">{{ confirmedOrCompletedWrestlerNames || 'No wrestlers' }}</p>
        </div>
      </div>
      <a :href="eventDetailUrl" v-if="!forAppEmbed" class="text-gray-700 btn btn-compact btn-default text-10 flex-shrink-0">
        <i class="fa fa-info-circle"></i> Details
      </a>
    </div>

    <!-- Lesson notes (if available) -->
    <div v-if="privateLesson.notes" class="private-lesson-notes p-x-sm p-b-sm">
      <div class="" v-html="privateLesson.notes">
      </div>
    </div>

    <!-- Placeholder when no notes -->
    <div v-else class="text-gray-400 italic p-x-sm p-b-sm">
      No lesson notes available
    </div>
  </div>
</template>

<script>
import WrestlerIcon from '../../shared/wrestler-icon.vue';
import { appEmbedMix } from './mixins/app_embed_mix';
export default {
  name: 'PrivateLessonCard',
  components: { WrestlerIcon },
  mixins: [appEmbedMix],
  props: {
    privateLesson: {
      type: Object,
      required: true
    },
    wrestlerId: {
      type: Number,
      required: false
    }
  },
  computed: {
    firstEventBookings() {
      return this.privateLesson.events[0].event_bookings;
    },
    coachEventBookings() {
      return this.firstEventBookings.filter(booking => booking.profile_type === 'CoachProfile');
    },
    coachNames() {
      return this.coachEventBookings.map(booking => booking.profile.display_name).join(', ');
    },
    confirmedOrCompletedWrestlerEventBookings() {
      return this.firstEventBookings.filter(booking => booking.profile_type === 'WrestlerProfile' && (booking.status === 'confirmed' || booking.status === 'completed'));
    },
    confirmedOrCompletedWrestlerNames() {
      return this.confirmedOrCompletedWrestlerEventBookings.map(booking => booking.profile.display_name).join(', ');
    },
    currentWrestlerEventBooking() {
      return this.firstEventBookings.find(booking => booking.profile_type === 'WrestlerProfile' && booking.profile_id === this.wrestlerId);
    },
    statusLabel() {
      switch(this.currentWrestlerEventBooking.status) {
        case 'confirmed': return 'Confirmed';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        case 'pending': return 'Pending';
        case 'invited': return 'Invited';
        case 'declined': return 'Declined';
        default: return this.currentWrestlerEventBooking.status;
      }
    },
    showStatusBar() {
      const s = this.currentWrestlerEventBooking.status;
      return [
        'completed',
        'confirmed',
        'invited',
        'declined',
        'cancelled',
        'pending'
      ].includes(s);
    },
    statusClasses() {
      // No longer used for the main header background, but kept for possible future use
      switch(this.currentWrestlerEventBooking.status) {
        case 'confirmed': return 'bg-green-200 text-green-900';
        case 'completed': return 'bg-green-800 text-green-50';
        case 'cancelled': return 'bg-red-700 text-red-50';
        case 'pending': return 'bg-gray-600 text-yellow-50';
        case 'invited': return 'bg-blue-600 text-blue-50';
        case 'declined': return 'bg-orange-600 text-orange-50';
        default: return 'bg-gray-600 text-gray-50';
      }
    },
    firstEvent() {
      return this.privateLesson.events && this.privateLesson.events.length > 0
        ? this.privateLesson.events[0]
        : null;
    },
    eventStartAt() {
      return this.firstEvent ? this.firstEvent.start_at : null;
    },
    eventEndAt() {
      return this.firstEvent ? this.firstEvent.end_at : null;
    },
    eventDayOfWeek() {
      if (!this.eventStartAt) return '';
      const startDate = new Date(this.eventStartAt);
      return startDate.toLocaleDateString('en-US', { weekday: 'short' });
    },
    eventMonthDay() {
      if (!this.eventStartAt) return '';
      const startDate = new Date(this.eventStartAt);
      return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    eventTime() {
      if (!this.eventStartAt || !this.eventEndAt) return '';
      const startDate = new Date(this.eventStartAt);
      const endDate = new Date(this.eventEndAt);
      const startTime = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      const endTime = endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      return `${startTime} - ${endTime}`;
    },
    eventLocation() {
      return this.firstEvent ? this.firstEvent.location : null;
    },
    eventDetailUrl() {
      return this.firstEvent ? `/events/${this.firstEvent.id}` : '#';
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'No date';
      return moment(dateString).format('MMM D, YYYY h:mm A');
    },
    formatEventDateTime(start, end) {
      if (!start || !end) return 'No date';
      const startDate = new Date(start);
      const endDate = new Date(end);
      const day = startDate.toLocaleDateString('en-US', { weekday: 'short' });
      const date = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const startTime = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      const endTime = endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      return `${day} ${date}, ${startTime} - ${endTime}`;
    },
  }
};
</script>
